import { motion } from 'framer-motion';
import {
  CheckCircle,
  Circle,
  ArrowRight,
  Sparkles,
  Building,
  FileText,
  Clock,
  Camera,
  Scissors,
  UserPlus,
  PlayCircle,
} from 'lucide-react';
import { NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';

interface Paso {
  titulo: string;
  completado: boolean;
  ruta: string;
}

interface Props {
  pasos: Paso[];
  porcentaje: number;
  showChecklist: boolean;
  navigate: NavigateFunction;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const ICONOS = [
  <Building className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
  <FileText className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
  <Clock className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
  <Camera className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
  <Scissors className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
  <UserPlus className="w-4 h-4 text-[#103254] dark:text-blue-400" />,
];

const DashboardChecklist = ({
  pasos,
  porcentaje,
  showChecklist,
  navigate,
}: Props) => {
  const { t } = useTranslation();

  if (!showChecklist) return null;

  const completados = pasos.filter((p) => p.completado).length;
  const siguientePaso = pasos.find((p) => !p.completado);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="w-full min-h-[80vh] flex items-center justify-center py-8"
    >
      <div className="w-full max-w-xl">
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#103254]/10 dark:bg-blue-400/10 text-[#103254] dark:text-blue-400 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {t('dashboardChecklist.badge')}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('dashboardChecklist.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            {t('dashboardChecklist.subtitle')}
          </p>
        </motion.div>

        {/* Card principal */}
        <motion.div variants={itemVariants}>
          <Card
            className={`relative isolate rounded-2xl ${GLASS_CARD} overflow-hidden transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(16,50,84,0.35)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]`}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/15 z-10"
            />
            {/* Barra de progreso top */}
            <div className="h-1.5 bg-gray-100/70 dark:bg-gray-700/70">
              <motion.div
                className="h-full bg-gradient-to-r from-[#103254] to-blue-500"
                initial={{ width: 0 }}
                animate={{ width: `${porcentaje}%` }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
              />
            </div>

            <CardContent className="px-6 py-5">
              {/* Progreso texto */}
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('dashboardChecklist.progress', {
                    done: completados,
                    total: pasos.length,
                  })}
                </span>
                <span className="text-xs font-bold text-[#103254] dark:text-blue-400 bg-[#103254]/10 dark:bg-blue-400/10 px-2 py-0.5 rounded-full">
                  {porcentaje}%
                </span>
              </div>

              {/* Lista de pasos */}
              <ul className="space-y-2">
                {pasos.map((p, i) => (
                  <motion.li
                    key={i}
                    variants={itemVariants}
                    onClick={() => !p.completado && navigate(p.ruta)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${
                      p.completado
                        ? 'bg-green-50/70 dark:bg-green-900/10 border-green-100/70 dark:border-green-900/30'
                        : siguientePaso === p
                        ? 'bg-[#103254]/5 dark:bg-blue-400/5 border-[#103254]/20 dark:border-blue-400/20 cursor-pointer hover:bg-[#103254]/10 dark:hover:bg-blue-400/10 hover:border-[#103254]/30'
                        : 'bg-white/40 dark:bg-gray-900/30 border-gray-100/70 dark:border-gray-700/50 cursor-pointer hover:bg-white/60 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    {/* Icono estado */}
                    <div className="shrink-0">
                      {p.completado ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle
                          className={`w-5 h-5 ${
                            siguientePaso === p
                              ? 'text-[#103254] dark:text-blue-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      )}
                    </div>

                    {/* Icono contextual */}
                    <span
                      className={`shrink-0 ${
                        p.completado
                          ? 'text-green-500'
                          : siguientePaso === p
                          ? 'text-[#103254] dark:text-blue-400'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    >
                      {ICONOS[i]}
                    </span>

                    {/* Texto */}
                    <span
                      className={`flex-1 text-sm font-medium ${
                        p.completado
                          ? 'text-green-700 dark:text-green-400 line-through decoration-green-400/50'
                          : siguientePaso === p
                          ? 'text-[#103254] dark:text-blue-300'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {p.titulo}
                    </span>

                    {/* CTA */}
                    {!p.completado && (
                      <div
                        className={`shrink-0 transition-colors ${
                          siguientePaso === p
                            ? 'text-[#103254] dark:text-blue-400'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </motion.li>
                ))}
              </ul>
            </CardContent>

            {/* Footer con siguiente acción destacada */}
            {siguientePaso && (
              <div className="px-6 py-4 bg-white/40 dark:bg-gray-900/30 border-t border-gray-100/70 dark:border-gray-700">
                <button
                  onClick={() => navigate(siguientePaso.ruta)}
                  className="w-full flex items-center justify-center gap-2 bg-[#103254] hover:bg-[#0d2742] text-white text-sm font-semibold py-2.5 rounded-xl transition-colors shadow-md shadow-[#103254]/20"
                >
                  {t('dashboardChecklist.continue')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Vídeo tutorial */}
        <motion.div variants={itemVariants} className="mt-5">
          <a
            href="https://www.youtube.com/watch?v=GPqF5eRywY0"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 px-5 py-4 rounded-2xl ${GLASS_CARD} hover:border-[#103254]/30 dark:hover:border-blue-400/30 transition-all duration-200 group`}
          >
            <div className="w-8 h-8 bg-[#103254]/10 dark:bg-blue-400/10 rounded-lg flex items-center justify-center shrink-0">
              <PlayCircle className="w-4 h-4 text-[#103254] dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('dashboardChecklist.tutorialTitle')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('dashboardChecklist.tutorialSubtitle')}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#103254] dark:group-hover:text-blue-400 transition-colors shrink-0" />
          </a>
        </motion.div>

        {/* Hint */}
        <motion.p
          variants={itemVariants}
          className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4"
        >
          {t('dashboardChecklist.hint')}
        </motion.p>
      </div>
    </motion.div>
  );
};

export default DashboardChecklist;